import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  FileText,
  User,
  Link2,
  CheckSquare,
  ChevronDown,
  AlertCircle,
  Calendar,
  Filter,
} from 'lucide-react';
import { useThemeStore } from '../../store/themeStore';
import { activityAPI, getErrorMessage } from '../../services/api';

interface ActivityLog {
  id: string;
  user_id: string;
  user_name: string;
  action: string;
  entity_type: string;
  entity_id?: string;
  entity_name?: string;
  metadata?: Record<string, unknown>;
  created_at: string;
}

interface ActivityResponse {
  activities: ActivityLog[];
  pagination: {
    offset: number;
    limit: number;
    total: number;
    has_more: boolean;
  };
}

const ACTION_DESCRIPTIONS: Record<string, string> = {
  'post.created': 'Created a post',
  'post.published': 'Published a post',
  'post.scheduled': 'Scheduled a post',
  'post.deleted': 'Deleted a post',
  'approval.submitted': 'Submitted for approval',
  'approval.approved': 'Approved a post',
  'approval.rejected': 'Rejected a post',
  'team.member_invited': 'Invited team member',
  'team.member_removed': 'Removed team member',
  'account.connected': 'Connected account',
  'account.disconnected': 'Disconnected account',
};

const getActionIcon = (action: string): React.ReactNode => {
  switch (action.split('.')[0]) {
    case 'post':
      return <FileText className="w-4 h-4" />;
    case 'team':
      return <User className="w-4 h-4" />;
    case 'account':
      return <Link2 className="w-4 h-4" />;
    case 'approval':
      return <CheckSquare className="w-4 h-4" />;
    default:
      return <AlertCircle className="w-4 h-4" />;
  }
};

const getActionColor = (action: string): string => {
  const category = action.split('.')[0];
  switch (category) {
    case 'post':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'team':
      return 'bg-purple-50 text-purple-700 border-purple-200';
    case 'account':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'approval':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    default:
      return 'bg-gray-50 text-gray-700 border-gray-200';
  }
};

const formatRelativeTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  const intervals: Record<string, number> = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60,
  };

  for (const [key, value] of Object.entries(intervals)) {
    const interval = Math.floor(seconds / value);
    if (interval >= 1) {
      return `${interval} ${key}${interval > 1 ? 's' : ''} ago`;
    }
  }

  return 'just now';
};

export const ActivityLogPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useThemeStore();
  const [actionFilter, setActionFilter] = useState<string>('');
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const { data, isLoading, error } = useQuery<ActivityResponse>({
    queryKey: ['activity', actionFilter, offset],
    queryFn: () =>
      activityAPI
        .list({
          action_type: actionFilter || undefined,
          limit,
          offset,
        })
        .then(r => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const pc = theme.hex?.primary || '#10b981';

  if (error) {
    return (
      <div className="bg-gray-100 min-h-full">
        <div className="px-4 pt-3 pb-4">
          <div className="bg-white rounded-xl border border-gray-200 text-center py-10">
            <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center mx-auto mb-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <p className="text-sm font-bold text-gray-900 mb-1">
              {t('activity_load_failed', 'Failed to load activity logs')}
            </p>
            <p className="text-xs text-gray-500">
              {getErrorMessage(error, t('activity_refresh_page', 'Please try refreshing the page.'))}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const activities = data?.activities || [];
  const pagination = data?.pagination;
  const totalCount = pagination?.total || 0;
  const hasMore = pagination?.has_more || false;

  return (
    <div className="bg-gray-100 min-h-full">
      <div className="px-4 pt-3 pb-4">
        {/* Header */}
        <div className="mb-3">
          <h1 className="text-lg font-bold text-gray-900 mb-1">
            {t('activity_title', 'Activity Log')}
          </h1>
          <p className="text-xs text-gray-600">
            {t('activity_subtitle', 'View team activity and changes')}
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-3 mb-3">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <select
              value={actionFilter}
              onChange={(e) => {
                setActionFilter(e.target.value);
                setOffset(0);
              }}
              className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-300 text-xs"
            >
              <option value="">{t('activity_filter_all', 'All actions')}</option>
              <option value="post.created">{t('activity_filter_post_created', 'Post created')}</option>
              <option value="post.published">{t('activity_filter_post_published', 'Post published')}</option>
              <option value="post.scheduled">{t('activity_filter_post_scheduled', 'Post scheduled')}</option>
              <option value="post.deleted">{t('activity_filter_post_deleted', 'Post deleted')}</option>
              <option value="approval.submitted">{t('activity_filter_approval_submitted', 'Approval submitted')}</option>
              <option value="approval.approved">{t('activity_filter_approval_approved', 'Approval approved')}</option>
              <option value="approval.rejected">{t('activity_filter_approval_rejected', 'Approval rejected')}</option>
              <option value="team.member_invited">{t('activity_filter_member_invited', 'Member invited')}</option>
              <option value="team.member_removed">{t('activity_filter_member_removed', 'Member removed')}</option>
              <option value="account.connected">{t('activity_filter_account_connected', 'Account connected')}</option>
              <option value="account.disconnected">{t('activity_filter_account_disconnected', 'Account disconnected')}</option>
            </select>
          </div>
        </div>

        {/* Activity List */}
        {isLoading ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-xl border border-gray-200 p-3 animate-pulse"
              >
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-gray-200 rounded-lg flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-gray-200 rounded w-40" />
                    <div className="h-2 bg-gray-100 rounded w-60" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : activities.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 text-center py-12">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3"
              style={{ backgroundColor: `${pc}15` }}
            >
              <Calendar className="w-6 h-6" style={{ color: pc }} />
            </div>
            <p className="text-sm font-bold text-gray-900 mb-1">
              {t('activity_no_logs', 'No activity yet')}
            </p>
            <p className="text-xs text-gray-500">
              {actionFilter
                ? t('activity_no_logs_filtered', 'No activities match the selected filter')
                : t('activity_no_logs_description', 'Activity will appear here as your team works')}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {activities.map(activity => (
              <div
                key={activity.id}
                className={`bg-white rounded-xl border border-gray-200 p-3 hover:shadow-sm transition-shadow ${getActionColor(activity.action)}`}
              >
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${pc}20`, color: pc }}
                  >
                    {getActionIcon(activity.action)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        {/* User and action */}
                        <div className="text-xs text-gray-900 mb-0.5">
                          <span className="font-semibold">{activity.user_name}</span>
                          {' '}
                          <span className="text-gray-600">
                            {ACTION_DESCRIPTIONS[activity.action] || activity.action}
                          </span>
                        </div>

                        {/* Entity name */}
                        {activity.entity_name && (
                          <div className="text-[10px] text-gray-600 mb-1 truncate">
                            {t('activity_entity_label', '{{ entity }}')}:{' '}
                            <span className="font-medium text-gray-700">{activity.entity_name}</span>
                          </div>
                        )}

                        {/* Timestamp */}
                        <div className="text-[10px] text-gray-500 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatRelativeTime(activity.created_at)}
                        </div>
                      </div>

                      {/* Timestamp right-aligned for small screens */}
                      <div className="text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0">
                        {new Date(activity.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {activities.length > 0 && (
          <div className="mt-4 flex items-center justify-between">
            <p className="text-xs text-gray-600">
              {t('activity_showing', 'Showing {{ count }} of {{ total }} activities', {
                count: activities.length,
                total: totalCount,
              })}
            </p>

            <div className="flex gap-2">
              <button
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0 || isLoading}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                {t('activity_previous', 'Previous')}
              </button>

              <button
                onClick={() => setOffset(offset + limit)}
                disabled={!hasMore || isLoading}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors flex items-center gap-1"
              >
                {t('activity_next', 'Next')}
                <ChevronDown className="w-3 h-3 rotate-270" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLogPage;
